import React, { useState } from 'react';
import { router } from '@inertiajs/react';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DataTable, type ColumnProps } from '@/components/ui/data-table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import EndMatchDialog, {
  ADMIN_END_REASONS,
} from '@/pages/Matches/components/EndMatchDialog';
import { Users, UserCheck, UserX, Clock, AlertTriangle } from 'lucide-react';
import { AdminLayout } from '@/components/AdminLayout';

const END_REASON_LABELS: Record<string, string> = {
  placed_successfully: 'Placed successfully',
  mentee_unresponsive: 'Mentee unresponsive',
  no_longer_available: 'No longer available',
  other: 'Other',
};

interface Match {
  id: number;
  mentor: {
    id: number;
    name: string;
    email: string;
  };
  mentee: {
    id: number;
    name: string;
    email: string;
  };
  status: 'active' | 'completed' | 'ended';
  category: 'pre-health' | 'graduate';
  created_at: string;
  duration_days: number;
  end_reason: string | null;
  notes: string | null;
  placement_school: string | null;
  last_confirmed_at: string | null;
  is_stale: boolean;
  ended_by: { id: number; name: string } | null;
}

interface Props {
  matches: Match[];
  filters: {
    status?: string;
    category?: string;
  };
  stats: {
    total: number;
    active: number;
    completed: number;
    ended: number;
  };
}

export default function MatchesIndex({ matches, filters, stats }: Props) {
  const [selectedStatus, setSelectedStatus] = useState(filters.status || 'all');
  const [selectedCategory, setSelectedCategory] = useState(
    filters.category || 'all'
  );
  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  const [matchToEnd, setMatchToEnd] = useState<Match | null>(null);

  const handleFilterChange = (status: string, category: string) => {
    const params = new URLSearchParams();
    if (status !== 'all') params.set('status', status);
    if (category !== 'all') params.set('category', category);

    router.get(
      `/admin/matches?${params.toString()}`,
      {},
      {
        preserveState: true,
        preserveScroll: true,
      }
    );
  };

  const getStatusBadge = (status: Match['status']) => {
    const variants = {
      active: 'default',
      completed: 'secondary',
      ended: 'destructive',
    } as const;

    return (
      <Badge variant={variants[status] || 'secondary'}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getCategoryBadge = (category: Match['category']) => {
    return (
      <Badge variant="outline">
        {category === 'pre-health' ? 'Pre-Health' : 'Graduate'}
      </Badge>
    );
  };

  const columns: ColumnProps<Match>[] = [
    {
      header: 'Mentor',
      render: (match) => (
        <div>
          <p className="font-medium">{match.mentor.name}</p>
          <p className="text-sm text-gray-500">{match.mentor.email}</p>
        </div>
      ),
    },
    {
      header: 'Mentee',
      render: (match) => (
        <div>
          <p className="font-medium">{match.mentee.name}</p>
          <p className="text-sm text-gray-500">{match.mentee.email}</p>
        </div>
      ),
    },
    {
      header: 'Category',
      render: (match) => getCategoryBadge(match.category),
    },
    {
      header: 'Status',
      render: (match) => (
        <div className="flex items-center gap-2">
          {getStatusBadge(match.status)}
          {match.is_stale && (
            <Badge
              variant="outline"
              className="border-transparent bg-amber-50 text-amber-700"
            >
              <AlertTriangle className="h-3 w-3 mr-1" />
              Check-in
            </Badge>
          )}
        </div>
      ),
    },
    {
      header: 'Duration',
      render: (match) => (
        <div className="text-sm">
          <p className="font-medium">{match.duration_days} days</p>
          <p className="text-gray-500">
            Started{' '}
            {formatDistanceToNow(new Date(match.created_at), {
              addSuffix: true,
            })}
          </p>
        </div>
      ),
    },
    {
      header: '',
      render: (match) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSelectedMatch(match)}
        >
          Details
        </Button>
      ),
    },
  ];

  return (
    <AdminLayout title='Matches'>
      <div className="">
        {/* <div className="bg-white border-b"> */}
        {/*   <div className="max-w-7xl mx-auto px-6 py-6"> */}
        {/*     <div className="flex justify-between items-center"> */}
        {/*       <div> */}
        {/*         <h1 className="text-3xl font-bold text-gray-900">Matches</h1> */}
        {/*         <p className="text-gray-600 mt-2"> */}
        {/*           View and monitor active mentorship relationships */}
        {/*         </p> */}
        {/*       </div> */}
        {/*     </div> */}
        {/*   </div> */}
        {/* </div> */}

        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="space-y-10">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                        Total Matches
                      </p>
                      <p className="text-4xl font-light">{stats.total}</p>
                      <div className="h-1 w-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full" />
                    </div>
                    <Users className="h-5 w-5 text-purple-500 mt-1" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                        Active
                      </p>
                      <p className="text-4xl font-light">{stats.active}</p>
                      <div className="h-1 w-12 bg-gradient-to-r from-green-500 to-green-600 rounded-full" />
                    </div>
                    <UserCheck className="h-5 w-5 text-green-500 mt-1" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                        Completed
                      </p>
                      <p className="text-4xl font-light">{stats.completed}</p>
                      <div className="h-1 w-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full" />
                    </div>
                    <Clock className="h-5 w-5 text-blue-500 mt-1" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                        Ended
                      </p>
                      <p className="text-4xl font-light">{stats.ended}</p>
                      <div className="h-1 w-12 bg-gradient-to-r from-red-500 to-red-600 rounded-full" />
                    </div>
                    <UserX className="h-5 w-5 text-red-500 mt-1" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-3">
                <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">Status</span>
                <div className="inline-flex bg-gray-100 p-1 rounded-lg">
                  <button
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${selectedStatus === 'all'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                    onClick={() => {
                      setSelectedStatus('all');
                      handleFilterChange('all', selectedCategory);
                    }}
                  >
                    All
                  </button>
                  <button
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${selectedStatus === 'active'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                    onClick={() => {
                      setSelectedStatus('active');
                      handleFilterChange('active', selectedCategory);
                    }}
                  >
                    Active
                  </button>
                  <button
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${selectedStatus === 'completed'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                    onClick={() => {
                      setSelectedStatus('completed');
                      handleFilterChange('completed', selectedCategory);
                    }}
                  >
                    Completed
                  </button>
                  <button
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${selectedStatus === 'ended'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                    onClick={() => {
                      setSelectedStatus('ended');
                      handleFilterChange('ended', selectedCategory);
                    }}
                  >
                    Ended
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">Category</span>
                <div className="inline-flex bg-gray-100 p-1 rounded-lg">
                  <button
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${selectedCategory === 'all'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                    onClick={() => {
                      setSelectedCategory('all');
                      handleFilterChange(selectedStatus, 'all');
                    }}
                  >
                    All
                  </button>
                  <button
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${selectedCategory === 'pre-health'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                    onClick={() => {
                      setSelectedCategory('pre-health');
                      handleFilterChange(selectedStatus, 'pre-health');
                    }}
                  >
                    Pre-Health
                  </button>
                  <button
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${selectedCategory === 'graduate'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                    onClick={() => {
                      setSelectedCategory('graduate');
                      handleFilterChange(selectedStatus, 'graduate');
                    }}
                  >
                    Graduate
                  </button>
                </div>
              </div>
            </div>

            {/* Matches Table */}
            <Card className="border-2 border-gray-100 pt-0 pb-6">
              <CardHeader className="border-b border-gray-100 pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-medium">Mentorship Relationships</CardTitle>
                    <p className="text-sm text-gray-500 mt-1">
                      {matches.length} {matches.length === 1 ? 'match' : 'matches'} found
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {matches.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                      <Users className="h-8 w-8 text-gray-400" />
                    </div>
                    <p className="text-gray-900 font-medium mb-1">No matches found</p>
                    <p className="text-sm text-gray-500">Adjust your filters to see more results</p>
                  </div>
                ) : (
                  <DataTable
                    data={matches}
                    columns={columns}
                    emptyMessage="No matches found"
                  />
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Dialog
        open={selectedMatch !== null}
        onOpenChange={(open) => !open && setSelectedMatch(null)}
      >
        <DialogContent>
          {selectedMatch && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {getStatusBadge(selectedMatch.status)}
                  Match details
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-5 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">
                      Mentor
                    </p>
                    <p className="font-medium">{selectedMatch.mentor.name}</p>
                    <p className="text-gray-500">{selectedMatch.mentor.email}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">
                      Mentee
                    </p>
                    <p className="font-medium">{selectedMatch.mentee.name}</p>
                    <p className="text-gray-500">{selectedMatch.mentee.email}</p>
                  </div>
                </div>

                {selectedMatch.status === 'active' && (
                  <div className="border-t border-gray-100 pt-4">
                    <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">
                      Last check-in
                    </p>
                    {selectedMatch.last_confirmed_at ? (
                      <p className="font-medium">
                        {formatDistanceToNow(
                          new Date(selectedMatch.last_confirmed_at),
                          { addSuffix: true }
                        )}
                      </p>
                    ) : (
                      <p className="text-gray-500">Never confirmed</p>
                    )}
                    {selectedMatch.is_stale && (
                      <p className="flex items-center gap-1.5 text-amber-700 mt-2">
                        <AlertTriangle className="h-4 w-4" />
                        No check-in in 3+ months — may need follow-up
                      </p>
                    )}
                  </div>
                )}

                {selectedMatch.status === 'completed' && (
                  <div className="border-t border-gray-100 pt-4">
                    <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">
                      Placement school
                    </p>
                    <p className="font-medium">
                      {selectedMatch.placement_school || '—'}
                    </p>
                  </div>
                )}

                {selectedMatch.status === 'ended' && (
                  <div className="border-t border-gray-100 pt-4 space-y-4">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">
                        End reason
                      </p>
                      <p className="font-medium">
                        {selectedMatch.end_reason
                          ? END_REASON_LABELS[selectedMatch.end_reason] ||
                            selectedMatch.end_reason
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">
                        Mentor notes{' '}
                        <span className="normal-case text-gray-400">
                          (internal — not shared with mentee)
                        </span>
                      </p>
                      <p className="whitespace-pre-wrap text-gray-700">
                        {selectedMatch.notes || 'No notes provided.'}
                      </p>
                    </div>
                  </div>
                )}

                {selectedMatch.ended_by && (
                  <div className="border-t border-gray-100 pt-4">
                    <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">
                      Closed by
                    </p>
                    <p className="font-medium">{selectedMatch.ended_by.name}</p>
                  </div>
                )}
              </div>

              {selectedMatch.status === 'active' && (
                <DialogFooter>
                  <Button
                    variant="destructive"
                    onClick={() => {
                      setMatchToEnd(selectedMatch);
                      setSelectedMatch(null);
                    }}
                  >
                    End match
                  </Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {matchToEnd && (
        <EndMatchDialog
          matchId={matchToEnd.id}
          menteeName={matchToEnd.mentee.name}
          open={matchToEnd !== null}
          onOpenChange={(open) => !open && setMatchToEnd(null)}
          endpoint={`/admin/matches/${matchToEnd.id}/end_match`}
          reasons={ADMIN_END_REASONS}
          description={
            <>
              Close the mentorship between{' '}
              <span className="font-medium text-gray-700">
                {matchToEnd.mentor.name}
              </span>{' '}
              and{' '}
              <span className="font-medium text-gray-700">
                {matchToEnd.mentee.name}
              </span>
            </>
          }
        />
      )}
    </AdminLayout>
  );
}
